/**
 * GatewayStatusCard — at-a-glance gateway health on the property detail page.
 * Handles: no gateway, unprovisioned/provisioned (awaiting registration),
 * commissioning, active, inactive states.
 */
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Loader2, Wifi, Clock, Cpu, HardDrive, Plus } from 'lucide-react';
import { formatDistanceToNow } from 'date-fns';
import type { GatewayDevice } from '@/types/gateway';
import {
  LIFECYCLE_STATUS_CONFIG,
  OPERATIONAL_STATUS_CONFIG,
  getWifiQuality,
  formatUptime,
} from '@/types/gateway';

interface GatewayStatusCardProps {
  propertyId: string;
  device: GatewayDevice | null;
  isLoading: boolean;
  onAddGateway: () => void;
}

export function GatewayStatusCard({ propertyId, device, isLoading, onAddGateway }: GatewayStatusCardProps) {
  if (isLoading) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-8 flex items-center justify-center">
          <Loader2 className="w-5 h-5 animate-spin text-wt-text-secondary" />
        </CardContent>
      </Card>
    );
  }

  // No gateway linked
  if (!device) {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardContent className="py-6 flex flex-col items-center gap-3">
          <p className="text-sm text-wt-text-secondary">No gateway linked to this property</p>
          <Button size="sm" onClick={onAddGateway}>
            <Plus className="w-3.5 h-3.5 mr-1.5" />
            Add Gateway
          </Button>
        </CardContent>
      </Card>
    );
  }

  const lifecycle = LIFECYCLE_STATUS_CONFIG[device.lifecycle_status];
  const operational = OPERATIONAL_STATUS_CONFIG[device.operational_status];

  // Awaiting device registration
  if (device.lifecycle_status === 'unprovisioned' || device.lifecycle_status === 'provisioned') {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-wt-text-primary">Gateway</CardTitle>
            <Badge variant="outline" className={lifecycle.color}>{lifecycle.label}</Badge>
          </div>
        </CardHeader>
        <CardContent>
          <p className="text-sm text-wt-text-secondary">
            Awaiting device registration. Power on the gateway.
          </p>
        </CardContent>
      </Card>
    );
  }

  // Commissioning
  if (device.lifecycle_status === 'commissioning') {
    return (
      <Card className="bg-wt-bg-secondary border-border">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-wt-text-primary">Gateway</CardTitle>
            <Badge variant="outline" className={lifecycle.color}>{lifecycle.label}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-wt-amber-primary">Commissioning in progress</p>
          <Button size="sm" variant="outline" asChild>
            <Link to={`/app/property/${propertyId}/gateway`}>
              View Commissioning
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Inactive
  if (device.lifecycle_status === 'inactive') {
    return (
      <Card className="bg-wt-bg-secondary border-border opacity-60">
        <CardHeader className="pb-2">
          <div className="flex items-center justify-between">
            <CardTitle className="text-sm font-medium text-wt-text-primary">Gateway</CardTitle>
            <Badge variant="outline" className={lifecycle.color}>{lifecycle.label}</Badge>
          </div>
        </CardHeader>
        <CardContent className="space-y-3">
          <p className="text-sm text-wt-text-secondary">Gateway is inactive</p>
          <Button size="sm" variant="outline" asChild>
            <Link to={`/app/property/${propertyId}/gateway`}>
              Manage Gateway
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Active (full status card)
  const wifiQ = getWifiQuality(device.wifi_rssi);
  const heartbeatText = device.last_heartbeat_at
    ? formatDistanceToNow(new Date(device.last_heartbeat_at), { addSuffix: true })
    : 'Never';

  return (
    <Card className="bg-wt-bg-secondary border-border">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-sm font-medium text-wt-text-primary">Gateway</CardTitle>
          <div className="flex items-center gap-2">
            <Badge variant="outline" className={lifecycle.color}>{lifecycle.label}</Badge>
            <div className="flex items-center gap-1.5">
              <span className={`w-2 h-2 rounded-full ${operational.dotColor}`} />
              <span className={`text-xs ${operational.textColor}`}>{operational.label}</span>
            </div>
          </div>
        </div>
      </CardHeader>
      <CardContent className="space-y-2.5">
        <div className="grid grid-cols-2 gap-x-4 gap-y-2 text-sm">
          <div className="flex items-center gap-1.5 text-wt-text-secondary">
            <Cpu className="w-3.5 h-3.5" />
            <span>Firmware</span>
          </div>
          <span className="font-mono-numeric text-wt-text-primary text-right">
            {device.firmware_version ?? '--'}
          </span>

          <div className="flex items-center gap-1.5 text-wt-text-secondary">
            <Wifi className="w-3.5 h-3.5" />
            <span>WiFi</span>
          </div>
          <span className={`font-mono-numeric text-right ${wifiQ.color}`}>
            {device.wifi_rssi != null ? `${device.wifi_rssi} dBm` : '--'} ({wifiQ.label})
          </span>

          <div className="flex items-center gap-1.5 text-wt-text-secondary">
            <Clock className="w-3.5 h-3.5" />
            <span>Uptime</span>
          </div>
          <span className="font-mono-numeric text-wt-text-primary text-right">
            {formatUptime(device.uptime_seconds)}
          </span>

          <div className="flex items-center gap-1.5 text-wt-text-secondary">
            <Clock className="w-3.5 h-3.5" />
            <span>Heartbeat</span>
          </div>
          <span className="text-wt-text-primary text-right">{heartbeatText}</span>

          <div className="flex items-center gap-1.5 text-wt-text-secondary">
            <HardDrive className="w-3.5 h-3.5" />
            <span>Bus Devices</span>
          </div>
          <span className="font-mono-numeric text-wt-text-primary text-right">
            {device.bus_devices_responding ?? 0}/{device.bus_devices_expected ?? '?'}
          </span>
        </div>

        <div className="pt-1">
          <Button size="sm" variant="outline" className="w-full" asChild>
            <Link to={`/app/property/${propertyId}/gateway`}>
              View Details
            </Link>
          </Button>
        </div>
      </CardContent>
    </Card>
  );
}
